import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { ProfileDisplayComponent } from '../profile-display/profile-display.component';
import {
  NgSelectComponent,
  NgSelectModule,
} from '@ng-select/ng-select';
import { Observable, Subscription } from 'rxjs';
import { DropdownService } from '@shared/service/dropdown.service';
import { buildSearchByKeysFunc } from '@shared/utils/utils';

@Component({
  selector: 'app-department-dropdown-std',
  templateUrl: './department-dropdown-std.component.html',
  styleUrls: ['./department-dropdown-std.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ProfileDisplayComponent,
    NgSelectModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DepartmentDropdownStdComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentDropdownStdComponent
  implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit
{
  @Input() clearable = true;
  @Input() placeholder = 'SIDEBAR.DEPARTMENT';
  @Input() canUseCache = true;
  @Input() reloadChoices: Observable<void> | undefined;
  @Input() isInvalid = false;

  @ViewChild(NgSelectComponent, { read: ElementRef })
  ngSelectComp?: ElementRef<HTMLElement>;

  departmentChoices = signal<DepartmentChoices>([]);
  selected = signal<number | null | undefined>(null);
  lang = signal<string>('en');
  loading = signal<boolean>(false);
  isDisabled = signal<boolean>(false);
  customSearchFn = buildSearchByKeysFunc([
    'context.display_en',
    'context.display',
    'context.department_code',
  ]);
  subscription = new Subscription();

  private _onTouch: () => void;
  private _onChange: (val: any) => void;

  private translate = inject(TranslateService);
  private dropdownApi = inject(DropdownService);

  constructor() {
    this.lang.set(this.translate.currentLang);
    this.setPlaceholder();
    this.subscription.add(
      this.translate.onLangChange.subscribe((event) => {
        this.lang.set(event.lang);
        this.setPlaceholder();
      }),
    );
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouch = fn;
  }

  writeValue(val: number | null | undefined): void {
    if (val === null || val === undefined) {
      this.selected.set(null);
    } else {
      this.selected.set(Number(val));
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  ngOnInit(): void {
    this.fetchDepartmentChoices();
    this.subscribeReloadChoice();
  }

  ngAfterViewInit(): void {
    this.setPlaceholder();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  subscribeReloadChoice() {
    if (this.reloadChoices) {
      this.subscription.add(
        this.reloadChoices.subscribe(() => {
          this.fetchDepartmentChoices(false);
        }),
      );
    }
  }

  fetchDepartmentChoices(useCache = this.canUseCache): void {
    this.loading.set(true);
    const subs = this.dropdownApi
      .getAllDepartmentDropdown(useCache)
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          this.departmentChoices.set(res);
        },
      });
    this.subscription.add(subs);
  }

  onSelectItem(val: number | null | undefined): void {
    this.selected.set(val);
    this._onChange(val);
  }

  setPlaceholder(): void {
    const typeaheadInput =
      this.ngSelectComp?.nativeElement.querySelector('.ng-input')
        ?.children[0] as HTMLInputElement | undefined;
    if (typeaheadInput) {
      typeaheadInput.placeholder =
        this.translate.instant('COMMON.Search') + '...';
    }
  }
}

type DepartmentChoices = ReturnType<
  DropdownService['getAllDepartmentDropdown']
> extends Observable<infer T>
  ? T
  : never;

type DepartmentChoice = DepartmentChoices extends Array<infer T>
  ? T
  : never;
